import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { CouponRuleHistoryService } from './coupon-rule-history.service';
import {
  CouponRuleHistoryView,
  couponDialogError,
  couponRuleHistoryParams,
  EMPTY_RULE_HISTORY_MESSAGE,
  formatCouponValidationError,
  isHttpStatus,
  ruleInEffect,
  toCouponRuleHistoryView
} from './coupon-rule-history.util';

@Component({
  selector: 'app-coupon-rule-history',
  templateUrl: './coupon-rule-history.component.html',
  styleUrl: './coupon-rule-history.component.scss'
})
export class CouponRuleHistoryComponent implements OnInit {
  @Input({ required: true }) couponId!: number;

  private historyService = inject(CouponRuleHistoryService);

  filterForm = new FormGroup({
    from: new FormControl<Date | null>(null),
    to: new FormControl<Date | null>(null)
  });

  effectiveForm = new FormGroup({
    at: new FormControl<Date | null>(null, Validators.required)
  });

  entries: CouponRuleHistoryView[] = [];
  effectiveEntry: CouponRuleHistoryView | null = null;
  historyLoaded = false;
  historyEmpty = false;
  effectiveLoaded = false;
  effectiveEmpty = false;
  validationMessage = '';
  emptyHistoryMessage = EMPTY_RULE_HISTORY_MESSAGE;

  async ngOnInit(): Promise<void> {
    await this.search();
  }

  async search(): Promise<void> {
    const query = couponRuleHistoryParams(this.filterForm.getRawValue());
    if (query.error) {
      this.validationMessage = query.error;
      return;
    }
    await this.load('history', query);
  }

  async loadEffective(): Promise<void> {
    if (this.effectiveForm.invalid) {
      this.effectiveForm.controls.at.markAsTouched();
      this.validationMessage = 'Informe a data para ver a regra vigente.';
      return;
    }
    const query = couponRuleHistoryParams({
      to: this.effectiveForm.controls.at.value
    });
    if (query.error) {
      this.validationMessage = query.error;
      return;
    }
    await this.load('effective', query);
  }

  clearFilters(): void {
    this.filterForm.reset({ from: null, to: null });
    this.validationMessage = '';
    void this.search();
  }

  private async load(
    target: 'history' | 'effective',
    query: { from?: string; to?: string }
  ): Promise<void> {
    this.validationMessage = '';
    LoaderService.showLoader();
    try {
      const entries = await lastValueFrom(
        this.historyService.list(this.couponId, query)
      );
      if (target === 'history') {
        this.entries = entries.map((entry, index) =>
          toCouponRuleHistoryView(entry, index)
        );
        this.historyLoaded = true;
        this.historyEmpty = this.entries.length === 0;
        return;
      }
      const current = ruleInEffect(entries);
      this.effectiveEntry = current
        ? toCouponRuleHistoryView(current, 0)
        : null;
      this.effectiveLoaded = true;
      this.effectiveEmpty = this.effectiveEntry == null;
    } catch (error: unknown) {
      const message = formatCouponValidationError(error);
      if (isHttpStatus(error, 400) || isHttpStatus(error, 404)) {
        this.validationMessage = message;
      }
      AppDialogService.showErrorDialog(couponDialogError(error, message));
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
